import { makeAutoObservable, runInAction } from 'mobx';
import { Banner } from '../shared/models/Banner';
import { Project } from '../shared/models/Project';
import { Organization } from '../shared/models/Organization';
import { ProjectProgress, ProgressReviewStatus } from '../shared/models/ProjectProgress';
import { getBanners } from '../services/BannerService';
import { ProjectService } from '../services/ProjectService';
import { OrganizationService } from '../services/OrganizationService';
import { ProjectProgressService } from '../services/ProjectProgressService';
import { ProjectListParams, ProjectProgressListParams, PaginatedResponse } from '../types/api';

/**
 * 首页 ViewModel（H5 用户端）
 */
export class HomeViewModel {
  /** 机构信息 */
  organization: Organization | null = null;
  
  /** Banner 列表 */
  banners: Banner[] = [];
  
  /** 项目列表（首页请求 1 页，部分展示） */
  projects: Project[] = [];

  /** 项目分页信息（用于“查看更多”判断） */
  projectPagination = {
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
  };

  /** 项目是否还有更多 */
  projectHasMore = false;

  /** 首页展示项目数量（可调，默认 3） */
  projectDisplayCount = 3;

  /** 项目进展（时间轴） */
  progresses: ProjectProgress[] = [];
  /** 项目进展去重后全集（每个项目只保留最新） */
  progressAll: ProjectProgress[] = [];
  /** 首页进展展示条数（每次 +3） */
  progressDisplayCount = 3;

  /** 项目进展分页 */
  progressPagination = {
    page: 1,
    limit: 4,
    total: 0,
    totalPages: 0,
  };

  /** 项目进展加载状态 */
  progressLoading = false;

  /** 项目进展是否还有更多 */
  progressHasMore = false;
  
  /** 机构加载状态 */
  organizationLoading = false;
  
  /** Banner 加载状态 */
  bannerLoading = false;
  
  /** 项目加载状态 */
  projectLoading = false;
  
  /** 错误信息 */
  error: string | null = null;

  constructor() {
    makeAutoObservable(this, {}, { autoBind: true });
  }

  /**
   * 加载 Banner
   */
  async loadBanners(organizationId?: string) {
    this.bannerLoading = true;
    this.error = null;

    try {
      const banners = await getBanners(organizationId);
      runInAction(() => {
        // 确保是数组，然后按 sortOrder 降序排列
        if (Array.isArray(banners)) {
          this.banners = banners.sort((a, b) => (b.sortOrder || 0) - (a.sortOrder || 0));
        } else {
          this.banners = [];
        }
        this.bannerLoading = false;
      });
    } catch (err) {
      console.error('HomeViewModel: 加载 Banner 失败:', err);
      runInAction(() => {
        this.error = err instanceof Error ? err.message : '加载 Banner 失败';
        this.bannerLoading = false;
      });
    }
  }

  /**
   * 加载项目列表（首页只显示前几个）
   */
  async loadProjects(limit: number = 10, displayCount?: number, organizationId?: string, organizationType?: string) {
    this.projectLoading = true;
    this.error = null;

    try {
      const queryParams: ProjectListParams = {
        page: 1,
        limit,
      };
      
      // 根据机构类型传递正确的参数
      if (organizationType === 'EXECUTING') {
        queryParams.executingOrganizationId = organizationId;
      } else {
        queryParams.organizationId = organizationId;
      }

      const response: PaginatedResponse<Project> = await ProjectService.getProjects(queryParams);

      runInAction(() => {
        if (response && response.list) {
          // 拆分发布和停售，发布在前，停售在后
          const published = response.list.filter((p) => p.status === 'PUBLISHED');
          const stopped = response.list.filter((p) => p.status === 'STOPPED');
          const sortFn = (a: Project, b: Project) => {
            if (a.isPinned !== b.isPinned) return (b.isPinned ? 1 : 0) - (a.isPinned ? 1 : 0);
            const at = new Date(a.updateTime || a.createTime || '').getTime() || 0;
            const bt = new Date(b.updateTime || b.createTime || '').getTime() || 0;
            return bt - at;
          };
          const sortedPublished = published.sort(sortFn);
          const sortedStopped = stopped.sort(sortFn);
          this.projects = [...sortedPublished, ...sortedStopped];
          
          // 使用后端返回的分页信息（基于所有符合条件的项目总数）
          this.projectPagination = {
            page: response.page,
            limit: response.limit,
            total: response.total, // 使用后端返回的 total
            totalPages: response.totalPages, // 使用后端返回的 totalPages
          };
          
          // 根据后端返回的分页信息判断是否还有更多数据
          // 如果当前页小于总页数，说明还有更多
          this.projectHasMore = response.page < response.totalPages;
          
          if (typeof displayCount === 'number') {
            this.projectDisplayCount = displayCount;
          }
        } else {
          this.projects = [];
          this.projectPagination = { page: 1, limit, total: 0, totalPages: 0 };
          this.projectHasMore = false;
        }
        this.projectLoading = false;
      });
    } catch (err) {
      console.error('HomeViewModel: 加载项目失败:', err);
      runInAction(() => {
        this.error = err instanceof Error ? err.message : '加载项目列表失败';
        this.projectLoading = false;
      });
    }
  }

  /**
   * 加载项目进展（首页时间轴展示）
   */
  async loadProjectProgresses(limit: number = 20, organizationId?: string, organizationType?: string) {
    this.progressLoading = true;
    this.error = null;

    try {
      const params: ProjectProgressListParams = {
        page: 1,
        limit,
        reviewStatus: ProgressReviewStatus.APPROVED as any,
      };
      
      // 根据机构类型传递正确的参数
      if (organizationId) {
        params.organizationId = organizationId;
        if (organizationType) {
          params.organizationType = organizationType;
        }
      }

      const response = await ProjectProgressService.getProjectProgresses(params);

      runInAction(() => {
        const approvedList = (response.list || []).filter(
          (item) => item.reviewStatus === ProgressReviewStatus.APPROVED
        );
        // 过滤未来公布时间（仅展示已到公布时间的进展）
        const now = Date.now();
        const arrived = approvedList.filter((p) => {
          const t = new Date(p.progressDate || '').getTime();
          return !Number.isNaN(t) && t <= now;
        });
        
        // 1. 建立进展Map（projectId -> 最新进展）
        // 对于同一个项目的多条进展，保留最新的（按progressDate和updateTime）
        const progressMap = new Map<string, ProjectProgress>();
        for (const item of arrived) {
          const existing = progressMap.get(item.projectId);
          if (!existing) {
            progressMap.set(item.projectId, item);
          } else {
            // 比较时间，保留更新的
            const existingDate = new Date(existing.progressDate || '').getTime() || 0;
            const itemDate = new Date(item.progressDate || '').getTime() || 0;
            if (itemDate > existingDate || 
                (itemDate === existingDate && 
                 new Date(item.updateTime || '').getTime() > new Date(existing.updateTime || '').getTime())) {
              progressMap.set(item.projectId, item);
            }
          }
        }
        
        // 2. 先收集所有有真实进展的项目进展，按项目列表顺序
        const realProgresses: ProjectProgress[] = [];
        
        // 3. 再收集首页前N个项目的进展，按项目列表顺序
        const homePageProgresses: ProjectProgress[] = [];
        
        if (this.projects && this.projects.length > 0) {
          // 遍历所有已发布的项目（按项目列表顺序）
          const publishedProjects = this.projects.filter(p => p.status === 'PUBLISHED');
          
          // 首页展示的项目数量（默认3个）
          const homePageCount = this.projectDisplayCount || 3;
          const homePageProjects = publishedProjects.slice(0, homePageCount);
          
          // 先收集所有有真实进展的项目进展（按项目列表顺序）
          for (const project of publishedProjects) {
            const progress = progressMap.get(project.id);
            if (progress) {
              realProgresses.push(progress);
            }
          }
          
          // 再收集首页前N个项目的进展（按项目列表顺序）
          for (const project of homePageProjects) {
            // 如果已有真实进展，跳过（已在realProgresses中）
            if (progressMap.has(project.id)) {
              continue;
            }
            // 如果没有进展，创建虚拟的"启动公益计划"进展
            if (project.publishTime) {
              const virtualProgress: ProjectProgress = {
                id: `virtual-launch-${project.id}`,
                projectId: project.id,
                projectName: project.title,
                title: '启动公益计划',
                content: '',
                images: [],
                progressDate: project.publishTime, // 使用 publishTime，不是 createTime
                reviewStatus: ProgressReviewStatus.APPROVED,
                createTime: project.publishTime,
                updateTime: project.publishTime,
              };
              homePageProgresses.push(virtualProgress);
          }
        }
        }
        
        // 4. 合并：有真实进展的在前（按项目列表顺序），首页前N个的在后（按项目列表顺序）
        const allProgresses = [...realProgresses, ...homePageProgresses];
        
        this.progressAll = allProgresses;
        this.progressDisplayCount = Math.min(3, allProgresses.length);
        this.progresses = allProgresses.slice(0, this.progressDisplayCount);
        this.progressPagination = {
          page: response.page,
          limit: response.limit,
          total: allProgresses.length,
          totalPages: Math.ceil(allProgresses.length / this.progressDisplayCount || 1),
        };
        this.progressHasMore = this.progressDisplayCount < allProgresses.length;
        this.progressLoading = false;
      });
    } catch (err) {
      runInAction(() => {
        this.error = err instanceof Error ? err.message : '加载项目进展失败';
        this.progressLoading = false;
      });
    }
  }

  /** 首页进展“查看更多”，一次追加 3 条 */
  loadMoreProgresses(step: number = 3) {
    const next = Math.min(this.progressDisplayCount + step, this.progressAll.length);
    this.progressDisplayCount = next;
    this.progresses = this.progressAll.slice(0, next);
    this.progressHasMore = next < this.progressAll.length;
  }

  /**
   * 加载机构信息（获取第一个公募机构）
   */
  async loadOrganization() {
    this.organizationLoading = true;
    this.error = null;

    try {
      const organizations = await OrganizationService.getOrganizations();
      
      runInAction(() => {
        // 确保 organizations 是数组
        const orgList = Array.isArray(organizations) ? organizations : [];
        
        // 获取第一个公募机构
        const publicOrg = orgList.find(org => org.type === 'PUBLIC_FUNDRAISING');
        if (publicOrg) {
          this.organization = publicOrg;
        } else if (orgList.length > 0) {
          // 如果没有公募机构，使用第一个机构
          this.organization = orgList[0];
        } else {
          this.organization = null;
        }
        this.organizationLoading = false;
      });
    } catch (err) {
      console.error('HomeViewModel: 加载机构失败:', err);
      runInAction(() => {
        this.error = err instanceof Error ? err.message : '加载机构信息失败';
        this.organizationLoading = false;
      });
    }
  }

  /**
   * 初始化加载
   */
  async init(organizationId?: string, organizationType?: string) {
    // 先加载项目列表，再加载进展（进展需要项目列表来生成虚拟进展）
    await Promise.all([
      // this.loadOrganization(), // 注释掉，机构信息已经从 OrganizationContext 获取
      this.loadBanners(organizationId),
      this.loadProjects(10, undefined, organizationId, organizationType),
    ]);
    // 项目列表加载完成后再加载进展
    await this.loadProjectProgresses(20, organizationId, organizationType);
  }

  /**
   * 重置状态
   */
  reset() {
    this.organization = null;
    this.banners = [];
    this.projects = [];
    this.projectPagination = { page: 1, limit: 10, total: 0, totalPages: 0 };
    this.projectHasMore = false;
    this.projectDisplayCount = 3;
    this.progresses = [];
    this.progressAll = [];
    this.progressDisplayCount = 3;
    this.progressPagination = { page: 1, limit: 4, total: 0, totalPages: 0 };
    this.progressHasMore = false;
    this.organizationLoading = false;
    this.bannerLoading = false;
    this.projectLoading = false;
    this.progressLoading = false;
    this.error = null;
  }

  /**
   * 首页展示用的项目子集
   */
  get displayProjects() {
    return this.projects.slice(0, this.projectDisplayCount);
  }
}

